import React, { Component } from 'react';

import { Popover } from 'reactstrap';
import UserSummaryCard from './UserSummaryCard';
import { connect } from 'react-redux';
import { copyToClipboard } from '../../utils';
import {
  fetchUserSummary,
  mergeUserSummary,
  normaliseUserSummary,
  userReference,
} from '../../services/userSummary.service';
import { showInfo } from '../../actions/notificationActions';

import './UserSummaryCard.css';

// Long enough that scanning a column of names doesn't fire a card (and a
// request) per row, short enough that a deliberate hover feels immediate.
const OPEN_DELAY = 350;
// The gap between the link and the card is dead space; the mouse has to cross it
// to reach the card, so closing has to lag behind leaving the link.
const CLOSE_DELAY = 200;

class UserLink extends Component {
  state = {
    open: false,
    // The authoritative record from `GET /user`. Null until the first hover
    // resolves — the card renders from the caller's seed until then.
    details: null,
    loading: false,
    failed: false,
  };

  anchor = React.createRef();

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
    this.clearTimers();
  }

  clearTimers = () => {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
  };

  scheduleOpen = () => {
    clearTimeout(this.closeTimer);
    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(this.open, OPEN_DELAY);
  };

  scheduleClose = () => {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(this.close, CLOSE_DELAY);
  };

  cancelClose = () => clearTimeout(this.closeTimer);

  open = () => {
    this.setState({ open: true });
    this.load();
  };

  close = () => this.setState({ open: false });

  // Only fetch once per mounted link. A failure leaves `details` null so the
  // next hover retries; the service handles caching across links and rows.
  load = () => {
    if (this.state.details || this.state.loading) return;

    this.setState({ loading: true, failed: false });

    fetchUserSummary(userReference(this.props.user))
      .then((details) => {
        if (!this.mounted) return;
        this.setState({ details, loading: false });
      })
      .catch(() => {
        if (!this.mounted) return;
        // Deliberately not toasted: a hover the admin may have already moved on
        // from shouldn't raise an error alert. The card says so instead.
        this.setState({ loading: false, failed: true });
      });
  };

  copy = (value, label) => {
    copyToClipboard(value);
    this.props.dispatch(showInfo(`${label} copied — ${value}`));
  };

  render() {
    const { user, children } = this.props;
    const { open, details, loading, failed } = this.state;

    // User may be missing on records whose relation was deleted or not embedded,
    // and an order placed against a phone with no account has nothing to look up
    // either — render the label on its own rather than a link to nowhere.
    const reference = userReference(user);
    if (!reference) {
      return <span>{children}</span>;
    }

    const link = `/users/${reference}`;
    const summary = mergeUserSummary(normaliseUserSummary(user), details);

    return (
      <React.Fragment>
        <a
          ref={this.anchor}
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          onMouseEnter={this.scheduleOpen}
          onMouseLeave={this.scheduleClose}
          onFocus={this.scheduleOpen}
          onBlur={this.scheduleClose}
          // Rows in several lists navigate on click; the link owns its own
          // destination.
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </a>

        {/* Mounted only while open so a 30-row table doesn't carry 30 idle
            popovers. It renders into document.body, which keeps it clear of the
            `overflow: hidden` on table cells. */}
        {open && (
          <Popover
            isOpen
            placement="right"
            target={() => this.anchor.current}
            toggle={this.close}
            className="user-summary-popover"
          >
            <div
              onMouseEnter={this.cancelClose}
              onMouseLeave={this.scheduleClose}
            >
              <UserSummaryCard
                summary={summary}
                loading={loading}
                failed={failed}
                profileUrl={link}
                onCopy={this.copy}
              />
            </div>
          </Popover>
        )}
      </React.Fragment>
    );
  }
}

export default connect()(UserLink);
